import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import MuiChip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useSiteContent } from '../context/SiteContentContext';

function SpecList({ subtitle, specs }) {
    return (
        <Typography variant="h6" sx={{ color: 'primary.main' }}>
            {subtitle}
            <Typography component="span" sx={{ color: 'text.primary', mt: -2 }}>
                <br />
                {specs.map(({ label, value }) => (
                    <React.Fragment key={label}>
                        <b>{label}:</b> {value}
                        <br />
                    </React.Fragment>
                ))}
            </Typography>
        </Typography>
    );
}

const Chip = styled(MuiChip)(({ theme }) => ({
    variants: [
        {
            props: ({ selected }) => selected,
            style: {
                background:
                    'linear-gradient(to bottom right, hsl(210, 98%, 48%), hsl(210, 98%, 35%))',
                color: 'hsl(0, 0%, 100%)',
                borderColor: (theme.vars || theme).palette.primary.light,
                '& .MuiChip-label': {
                    color: 'hsl(0, 0%, 100%)',
                },
                ...theme.applyStyles('dark', {
                    borderColor: (theme.vars || theme).palette.primary.dark,
                }),
            },
        },
    ],
}));

function MobileLayout({ items, selectedItemIndex, handleItemClick, selectedFeature }) {
    if (!items[selectedItemIndex]) {
        return null;
    }

    return (
        <Box
            sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                gap: 2,

            }}
        >
            <Box sx={{ display: 'flex', gap: 2, overflow: 'auto', }}>
                {items.map(({ title }, index) => (
                    <Chip
                        size="medium"
                        key={index}
                        label={title}
                        onClick={() => handleItemClick(index)}
                        selected={selectedItemIndex === index}
                    />
                ))}
            </Box>
            <Card variant="outlined">
                <Box
                    sx={(theme) => ({
                        mb: 2,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        aspectRatio: '4 / 3',
                        backgroundImage: 'var(--items-imageLight)',
                        ...theme.applyStyles('dark', {
                            backgroundImage: 'var(--items-imageDark)',
                        }),
                    })}
                    style={
                        items[selectedItemIndex]
                            ? {
                                '--items-imageLight': items[selectedItemIndex].imageLight,
                                '--items-imageDark': items[selectedItemIndex].imageDark,
                            }
                            : {}
                    }
                />
                <Box sx={{ px: 2, pb: 2 }}>
                    <Typography
                        gutterBottom
                        sx={{ color: 'text.primary', fontWeight: 'medium' }}
                    >
                        {selectedFeature.title}
                    </Typography>
                    <Box sx={{ color: 'text.secondary', mb: 1.5 }}>
                        {selectedFeature.description}
                    </Box>
                </Box>
            </Card>
        </Box>
    );
}

MobileLayout.propTypes = {
    items: PropTypes.array.isRequired,
    handleItemClick: PropTypes.func.isRequired,
    selectedFeature: PropTypes.shape({
        description: PropTypes.node.isRequired,
        icon: PropTypes.element,
        imageDark: PropTypes.string.isRequired,
        imageLight: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
    }).isRequired,
    selectedItemIndex: PropTypes.number.isRequired,
};

export { MobileLayout };

export default function Features({ capabilities: capabilitiesProp }) {
    const { capabilities: contextCapabilities } = useSiteContent();
    const capabilities = capabilitiesProp ?? contextCapabilities;
    const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);

    const items = capabilities.map((capability) => ({
        title: capability.title,
        description: <SpecList subtitle={capability.subtitle} specs={capability.specs} />,
        imageLight: `url(${capability.imageUrl})`,
        imageDark: `url(${capability.imageUrl})`,
    }));

    const handleItemClick = (index) => {
        setSelectedItemIndex(index);
    };

    const selectedFeature = items[selectedItemIndex];

    if (!selectedFeature) {
        return null;
    }

    return (
        <Box sx={{ padding: { sm: 4, md: 8 }, paddingLeft: { sm: 0, md: 0 }, paddingRight: { sm: 0, md: 8 }, background: 'linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(212, 210, 210, 0.76) 49%, rgba(255, 255, 255, 1) 100%)' }}>
            <Box sx={{ padding: 4, paddingLeft: { sm: 4, md: 6 }, border: 1, borderColor: 'divider', borderTopRightRadius: 25, borderBottomRightRadius: 25, backgroundColor: 'background.default' }}>
                <Box sx={{ width: { sm: '100%', md: '60%' }, }}>
                    <Typography
                        component="h2"
                        variant="h4"
                        gutterBottom
                        sx={{ color: 'text.primary' }}
                    >
                        Service capabilities
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: 'text.secondary', mb: { xs: 2, sm: 4 } }}
                    >
                        CNC machine all types of parts, from simple “as machined” workholdings to complex geometries.
                        Our manufacturers operate almost every kind of CNC mill and turning center, with EDM and grinders available on request.
                        We offer tolerances down to ±.020 mm (±.001 in) and lead times from 5 business days.
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row-reverse' },
                        gap: 2,
                    }}
                >
                    <div>
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                flexDirection: 'column',
                                gap: 2,
                                height: '100%',
                            }}
                        >
                            {items.map(({ icon, title, description }, index) => (
                                <Box
                                    key={index}
                                    component={Button}
                                    onClick={() => handleItemClick(index)}
                                    sx={[
                                        (theme) => ({
                                            p: 2,
                                            height: '100%',
                                            width: '100%',
                                            '&:hover': {
                                                backgroundColor: (theme.vars || theme).palette.action.hover,
                                            },
                                        }),
                                        selectedItemIndex === index && {
                                            backgroundColor: 'action.selected',
                                        },
                                    ]}
                                >
                                    <Box
                                        sx={[
                                            {
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'left',
                                                gap: 1,
                                                textAlign: 'left',
                                                textTransform: 'none',
                                                color: 'text.secondary',
                                            },
                                            selectedItemIndex === index && {
                                                color: 'text.primary',
                                            },
                                        ]}
                                    >
                                        {icon}

                                        <Typography variant="h4" >{title}</Typography>
                                        {description}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <MobileLayout
                            items={items}
                            selectedItemIndex={selectedItemIndex}
                            handleItemClick={handleItemClick}
                            selectedFeature={selectedFeature}
                        />
                    </div>
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            width: { xs: '100%', md: '70%' },
                            height: 'var(--items-image-height)',
                        }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                height: '100%',
                                width: '100%',
                                display: { xs: 'none', md: 'flex' },
                                pointerEvents: 'none',
                            }}
                        >
                            <Box
                                sx={(theme) => ({
                                    m: 'auto',
                                    width: { sm: '100%', md: 420 },
                                    height: { sm: 300, md: 500 },
                                    backgroundSize: 'contain',
                                    backgroundImage: 'var(--items-imageLight)',
                                    ...theme.applyStyles('dark', {
                                        backgroundImage: 'var(--items-imageDark)',
                                    }),
                                })}
                                style={
                                    items[selectedItemIndex]
                                        ? {
                                            '--items-imageLight': items[selectedItemIndex].imageLight,
                                            '--items-imageDark': items[selectedItemIndex].imageDark,
                                        }
                                        : {}
                                }
                            />
                        </Card>
                    </Box>
                </Box>
            </Box>
        </Box >
    );
}
